import React from 'react';
import { object, func, arrayOf, bool } from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { I18n } from 'utils/i18n';

const TemplateSelector = ({
  onTemplateSelect,
  selectedTemplate = null,
  templates = [],
  isLoading = false,
  error = null,
  requiresUpload = false,
  providesDocument = false,
}) => {
  const handleChange = event => {
    const templateId = event.target.value;
    const template = templates.find(t => (t.uuid || t.id || t._id) === templateId);
    onTemplateSelect(template);
  };

  const handleClear = event => {
    event.stopPropagation();
    onTemplateSelect(null);
  };

  const getTemplateId = template => {
    return template.uuid || template.id || template._id;
  };

  const getTemplateName = template => {
    return template.name || template.title || getTemplateId(template);
  };

  let endAdornment = null;
  if (isLoading) {
    endAdornment = <CircularProgress size={20} sx={{ mr: 2 }} />;
  } else if (selectedTemplate) {
    endAdornment = (
      <IconButton size="small" onClick={handleClear} sx={{ mr: 2 }} disabled={isLoading || error}>
        <ClearIcon fontSize="small" />
      </IconButton>
    );
  }

  // Show dropdown immediately with appropriate state
  return (
    <Box sx={{ width: '100%', mb: 2 }} onClick={e => e.stopPropagation()}>
      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {error?.data?.message || I18n.t('AcknowledgementCreate.TemplateSelector.error')}
        </Alert>
      )}

      <FormControl sx={{ width: '100%' }} size="small">
        <InputLabel id="template-select-label">
          {I18n.t('AcknowledgementCreate.TemplateSelector.label')}
        </InputLabel>
        <Select
          labelId="template-select-label"
          id="template-select"
          value={selectedTemplate ? getTemplateId(selectedTemplate) : ''}
          onChange={handleChange}
          label={I18n.t('AcknowledgementCreate.TemplateSelector.label')}
          disabled={isLoading || error || templates.length === 0}
          endAdornment={endAdornment}
          MenuProps={{
            onClick: e => {
              e.stopPropagation();
            },
          }}
        >
          {isLoading && (
            <MenuItem disabled>{I18n.t('AcknowledgementCreate.TemplateSelector.loading')}</MenuItem>
          )}
          {!isLoading && templates.length === 0 && (
            <MenuItem disabled>
              {I18n.t('AcknowledgementCreate.TemplateSelector.noTemplates')}
            </MenuItem>
          )}
          {!isLoading &&
            templates.map(template => (
              <MenuItem key={getTemplateId(template)} value={getTemplateId(template)}>
                {getTemplateName(template)}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      {selectedTemplate && requiresUpload && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {I18n.t('AcknowledgementCreate.TemplateSelector.requiresUpload')}
        </Alert>
      )}
      {selectedTemplate && providesDocument && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {I18n.t('AcknowledgementCreate.TemplateSelector.providesDocument')}
        </Alert>
      )}
    </Box>
  );
};

TemplateSelector.propTypes = {
  onTemplateSelect: func.isRequired,
  selectedTemplate: object,
  templates: arrayOf(object),
  isLoading: bool,
  error: object,
  requiresUpload: bool,
  providesDocument: bool,
};

export default TemplateSelector;
